import React, { useState, useEffect } from "react";import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Users,
  Upload,
  RefreshCw,
  Trash2,
  Mail,
  ClipboardList,
  AlertCircle,
  CheckCircle2,
  Loader2,
  Globe,
} from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GROUP_EMAIL = "difusion@colegiotslomas.org";

export default function AdminGoogleGroupsPage() {
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [syncPreview, setSyncPreview] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [csvFile, setCsvFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [activeJob, setActiveJob] = useState(null); // job en curso con progress
  const [syncResult, setSyncResult] = useState(null); // resultado fijo cuando no hay nada para agregar
  const [sheetSyncResult, setSheetSyncResult] = useState(null);
  const [sheetSyncing, setSheetSyncing] = useState(false);

  const token = localStorage.getItem("token");
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  // Polling del job activo
  useEffect(() => {
    if (
      !activeJob?.job_id ||
      activeJob.job_id === "iniciando" ||
      activeJob?.estado === "completado" ||
      activeJob?.estado === "error"
    ) {
      return;
    }
    const interval = setInterval(async () => {
      try {
        const r = await axios.get(`${API_URL}/google-groups/jobs/${activeJob.job_id}`, auth);
        setActiveJob(r.data);
        if (r.data.estado === "completado") {
          const noAgregables = r.data.no_agregables || 0;
          const partes = [
            `+${r.data.added} nuevos`,
            `${r.data.exists} ya estaban`,
          ];
          if (noAgregables > 0) partes.push(`${noAgregables} no agregables`);
          partes.push(`${r.data.errors} errores`);
          toast.success(`Sync terminado: ${partes.join(", ")}`);
          await loadMembers();
          await loadAudit();
          clearInterval(interval);
        } else if (r.data.estado === "error") {
          toast.error(
            "El sync falló: " + (r.data.error_message || "credenciales de Google inválidas")
          );
          clearInterval(interval);
        }
      } catch (e) {
        clearInterval(interval);
      }
    }, 2500);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeJob?.job_id, activeJob?.estado]);

  const resumeJob = async () => {
    if (!activeJob?.job_id) return;
    try {
      const r = await axios.post(
        `${API_URL}/google-groups/jobs/${activeJob.job_id}/resume`,
        {},
        auth
      );
      setActiveJob(r.data.job);
      toast.info("Job reanudado");
    } catch (e) {
      toast.error("Error reanudando: " + (e.response?.data?.detail || e.message));
    }
  };

  // Detectar si el job está "pegado" (sin update por más de 45s)
  const jobEstaPegado = React.useMemo(() => {
    if (!activeJob?.fecha_actualizacion || activeJob.estado === "completado") return false;
    const ultima = new Date(activeJob.fecha_actualizacion).getTime();
    return (Date.now() - ultima) > 45000;
  }, [activeJob]);

  const loadMembers = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API_URL}/google-groups/${GROUP_EMAIL}/members`, auth);
      setMembers(r.data.members || []);
    } catch (e) {
      toast.error("Error cargando miembros: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const loadAudit = async () => {
    try {
      const r = await axios.get(`${API_URL}/google-groups/audit-log?limit=50`, auth);
      setAudit(r.data.entries || []);
    } catch (e) {
      /* silent */
    }
  };

  useEffect(() => {
    loadMembers();
    loadAudit();
  }, []);

  const runSheetSync = async () => {
    try {
      setSheetSyncing(true);
      setSheetSyncResult(null);
      const r = await axios.post(
        `${API_URL}/google-groups/sync-suscriptores-sheet`,
        {},
        auth
      );
      setSheetSyncResult(r.data);
      if (r.data.error) {
        toast.error("Error: " + r.data.error);
      } else if (r.data.added > 0) {
        toast.success(`Sync Squarespace OK: +${r.data.added} nuevos suscriptores agregados al grupo`);
        await loadMembers();
        await loadAudit();
      } else {
        toast.info(`Sync OK. Sin nuevos suscriptores (${r.data.skipped || 0} ya estaban)`);
      }
    } catch (e) {
      toast.error("Error: " + (e.response?.data?.detail || e.message));
    } finally {
      setSheetSyncing(false);
    }
  };

  const previewSync = async () => {
    try {
      setLoading(true);
      const r = await axios.post(
        `${API_URL}/google-groups/sync-matriculados?dry_run=true`,
        {},
        auth
      );
      setSyncPreview(r.data);
      toast.success(`Se agregarían ${r.data.a_agregar} matriculados`);
    } catch (e) {
      toast.error("Error: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const runSync = async () => {
    if (!window.confirm(`Se van a agregar ${syncPreview?.a_agregar || "?"} matriculados al grupo. ¿Continuar?`))
      return;
    // Barra ACTIVA desde el mismo click — así no queda "clavada en 0%"
    // mientras el servidor arranca el job en background.
    const optimisticJob = {
      job_id: "iniciando",
      estado: "iniciando",
      tipo: "sync_matriculados",
      total: syncPreview?.a_agregar || 0,
      procesados: 0,
      added: 0,
      exists: 0,
      no_agregables: 0,
      errors: 0,
      porcentaje: 2, // barra visible desde el primer tick
      fecha_actualizacion: new Date().toISOString(),
    };
    setActiveJob(optimisticJob);
    setSyncResult(null);
    try {
      setLoading(true);
      const r = await axios.post(
        `${API_URL}/google-groups/sync-matriculados?dry_run=false`,
        {},
        auth
      );
      if (r.data.job_id) {
        toast.info(`Sync iniciado en background: ${r.data.total_a_agregar} emails`);
        // Reemplazar el optimista con el real
        setActiveJob({ ...r.data, porcentaje: r.data.porcentaje ?? 2 });
      } else if (r.data.estado === "nada_para_agregar") {
        // Panel FIJO con el resultado — no un toast pasajero.
        setActiveJob(null);
        setSyncResult({
          matriculados_con_email: r.data.matriculados_con_email || 0,
          miembros_actuales_del_grupo: r.data.miembros_actuales_del_grupo || 0,
          no_agregables_excluidos: r.data.no_agregables_excluidos || 0,
          fecha: new Date().toISOString(),
        });
        toast.success("Sincronización al día");
        await loadMembers();
      } else {
        setActiveJob(null);
        toast.info(r.data.mensaje || "Sin acciones para realizar");
        await loadMembers();
      }
      setSyncPreview(null);
    } catch (e) {
      // Al fallar, sacamos el placeholder optimista.
      setActiveJob(null);
      toast.error("Error: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const addOne = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) return toast.error("Email inválido");
    try {
      setLoading(true);
      const r = await axios.post(
        `${API_URL}/google-groups/${GROUP_EMAIL}/members?user_email=${encodeURIComponent(email)}`,
        {},
        auth
      );
      if (r.data.status === "added") toast.success(`✓ ${email} agregado al grupo`);
      else if (r.data.status === "exists") toast.info(`${email} ya era miembro`);
      setNewEmail("");
      await loadMembers();
      await loadAudit();
    } catch (e) {
      toast.error("Error: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const removeMember = async (email) => {
    if (!window.confirm(`¿Quitar a ${email} del grupo?`)) return;
    try {
      setLoading(true);
      const r = await axios.delete(
        `${API_URL}/google-groups/${GROUP_EMAIL}/members/${encodeURIComponent(email)}`,
        auth
      );
      if (r.data.status === "removed") toast.success(`✓ ${email} eliminado`);
      else toast.info(`${email} no era miembro`);
      await loadMembers();
      await loadAudit();
    } catch (e) {
      toast.error("Error: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const uploadCsv = async () => {
    if (!csvFile) return toast.error("Elegí un archivo CSV primero");
    try {
      setLoading(true);
      setImportResult(null);
      const fd = new FormData();
      fd.append("file", csvFile);
      const r = await axios.post(
        `${API_URL}/google-groups/${GROUP_EMAIL}/bulk-import`,
        fd,
        {
          headers: { ...auth.headers, "Content-Type": "multipart/form-data" },
        }
      );
      if (r.data.job_id) {
        toast.info(`Import iniciado en background: ${r.data.total_emails} emails`);
        setActiveJob(r.data);
      }
    } catch (e) {
      toast.error("Error subiendo CSV: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="admin-google-groups-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 mb-1 flex items-center gap-2">
          <Users className="w-6 h-6 text-purple-600" />
          Grupos de Difusión — Google Workspace
        </h1>
        <p className="text-sm text-gray-600">
          Sincronización de emails con <code className="bg-gray-100 px-2 py-0.5 rounded">{GROUP_EMAIL}</code>
        </p>
      </div>

      {/* Estado actual */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Mail className="w-5 h-5" /> Estado del grupo
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center gap-4">
            <Badge variant="outline" className="text-base py-1 px-3" data-testid="badge-total-miembros">
              {members.length} miembros
            </Badge>
            <Button
              onClick={loadMembers}
              variant="outline"
              size="sm"
              disabled={loading}
              data-testid="btn-refresh-members"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
              Refrescar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Panel FIJO de resultado — visible cuando la sincronización no requiere acciones */}
      {syncResult && !activeJob && (
        <Card className="border-green-500 bg-green-50" data-testid="sync-result-panel">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-green-900">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              Sincronización al día
            </CardTitle>
            <CardDescription className="text-green-800">
              {syncResult.miembros_actuales_del_grupo} ya son miembros y{" "}
              {syncResult.no_agregables_excluidos} no se pueden agregar
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div>
                <div className="text-gray-600 text-xs">Matriculados con email</div>
                <div className="font-bold text-lg text-gray-800" data-testid="sync-matriculados">
                  {syncResult.matriculados_con_email}
                </div>
              </div>
              <div>
                <div className="text-gray-600 text-xs">Ya en el grupo</div>
                <div className="font-bold text-lg text-green-700" data-testid="sync-ya-miembros">
                  {syncResult.miembros_actuales_del_grupo}
                </div>
              </div>
              <div>
                <div className="text-gray-600 text-xs" title="Mails con 404 de Google o formato inválido — no se pueden agregar">
                  No agregables (excluidos)
                </div>
                <div
                  className={`font-bold text-lg ${syncResult.no_agregables_excluidos ? "text-amber-600" : "text-gray-700"}`}
                  data-testid="sync-no-agregables"
                >
                  {syncResult.no_agregables_excluidos}
                </div>
              </div>
            </div>
            <div className="mt-3 flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSyncResult(null)}
                data-testid="sync-result-cerrar"
              >
                Cerrar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Job activo con progreso */}
      {activeJob && (
        <Card
          className={
            activeJob.estado === "error"
              ? "border-red-500 bg-red-50"
              : "border-purple-500 bg-purple-50"
          }
          data-testid="active-job-card"
        >
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              {activeJob.estado === "completado" ? (
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              ) : activeJob.estado === "error" ? (
                <AlertCircle className="w-5 h-5 text-red-600" />
              ) : (
                <Loader2 className="w-5 h-5 text-purple-600 animate-spin" />
              )}
              {activeJob.estado === "completado"
                ? "Sync completado"
                : activeJob.estado === "error"
                ? "Sync falló"
                : activeJob.estado === "iniciando"
                ? "Iniciando sync..."
                : "Sync en progreso..."}
            </CardTitle>
            <CardDescription>
              {activeJob.job_id && activeJob.job_id !== "iniciando" ? (
                <>Job {activeJob.job_id.substring(0, 8)}... · Tipo:{" "}
                {activeJob.tipo || (activeJob.total_a_agregar ? "sync_matriculados" : "bulk_import")}</>
              ) : (
                <>Preparando el trabajo en el servidor...</>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
              <div
                className={`h-full transition-all duration-500 flex items-center justify-center text-white text-xs font-bold ${
                  activeJob.estado === "error"
                    ? "bg-red-600"
                    : activeJob.estado === "completado"
                    ? "bg-green-600"
                    : "bg-purple-600"
                }`}
                style={{ width: `${activeJob.porcentaje || 0}%` }}
                data-testid="job-progress-bar"
              >
                {activeJob.porcentaje > 5 && `${activeJob.porcentaje}%`}
              </div>
            </div>
            {activeJob.estado === "error" && activeJob.error_message && (
              <div
                className="bg-red-100 border border-red-300 rounded p-3 text-sm text-red-800"
                data-testid="job-error-message"
              >
                <div className="font-medium mb-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" /> El sync no pudo ejecutarse
                </div>
                <div className="text-red-700">{activeJob.error_message}</div>
                <div className="text-xs text-red-600 mt-1">
                  Verificá las credenciales de Google (Service Account JSON) o los permisos del grupo.
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
              <div>
                <div className="text-gray-500 text-xs">Procesados</div>
                <div className="font-bold text-lg" data-testid="job-procesados">
                  {activeJob.procesados || 0} / {activeJob.total || activeJob.total_emails || activeJob.total_a_agregar}
                </div>
              </div>
              <div>
                <div className="text-gray-500 text-xs">Agregados</div>
                <div className="font-bold text-lg text-green-700" data-testid="job-added">{activeJob.added || 0}</div>
              </div>
              <div>
                <div className="text-gray-500 text-xs">Ya existían</div>
                <div className="font-bold text-lg text-gray-700" data-testid="job-exists">{activeJob.exists || 0}</div>
              </div>
              <div>
                <div className="text-gray-500 text-xs" title="Mails que Google no puede agregar (404) — no se reintentan">
                  No agregables
                </div>
                <div
                  className={`font-bold text-lg ${activeJob.no_agregables ? "text-amber-600" : "text-gray-700"}`}
                  data-testid="job-no-agregables"
                >
                  {activeJob.no_agregables || 0}
                </div>
              </div>
              <div>
                <div className="text-gray-500 text-xs">Errores</div>
                <div className={`font-bold text-lg ${activeJob.errors ? "text-red-600" : "text-gray-700"}`} data-testid="job-errors">
                  {activeJob.errors || 0}
                </div>
              </div>
            </div>
            {activeJob.error_samples?.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-red-600">
                  <AlertCircle className="w-3 h-3 inline mr-1" />
                  Ver primeros errores
                </summary>
                <ul className="mt-1 text-red-700 space-y-0.5">
                  {activeJob.error_samples.slice(0, 20).map((e, i) => (
                    <li key={i}>{e.email}: {e.error}</li>
                  ))}
                </ul>
              </details>
            )}
            {jobEstaPegado && activeJob.estado !== "completado" && (
              <div className="bg-amber-50 border border-amber-300 rounded p-3 text-sm space-y-2">
                <div className="flex items-center gap-2 text-amber-900 font-medium">
                  <AlertCircle className="w-4 h-4" />
                  El job está detenido (sin actualizaciones desde hace más de 45s)
                </div>
                <p className="text-xs text-amber-800">
                  Puede haber sido interrumpido por un reinicio del backend. El scheduler
                  automático debería retomarlo en el próximo minuto, o podés forzar ahora:
                </p>
                <Button
                  size="sm"
                  onClick={resumeJob}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="btn-resume-job"
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Reanudar ahora
                </Button>
              </div>
            )}
            {activeJob.estado === "completado" && (
              <Button variant="outline" size="sm" onClick={() => setActiveJob(null)}>
                Cerrar
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Sync con matriculados */}
      <Card className="border-purple-200 bg-purple-50/40">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-purple-600" /> Sincronizar matriculados de la app
          </CardTitle>
          <CardDescription>
            Agrega al grupo los matriculados que tienen email válido en la app y aún no son miembros.
            No elimina a nadie.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Button
              onClick={previewSync}
              disabled={loading}
              variant="outline"
              data-testid="btn-preview-sync"
            >
              1) Vista previa (dry-run)
            </Button>
            {syncPreview && (
              <Button
                onClick={runSync}
                disabled={loading || syncPreview.a_agregar === 0}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="btn-run-sync"
              >
                2) Ejecutar sync ({syncPreview.a_agregar} nuevos)
              </Button>
            )}
          </div>

          {syncPreview && (
            <div className="text-sm bg-white border rounded-lg p-3 space-y-1">
              <div><b>Matriculados con email en la app:</b> {syncPreview.matriculados_con_email}</div>
              <div><b>Ya en el grupo:</b> {syncPreview.miembros_actuales_del_grupo}</div>
              <div><b>A agregar:</b> <span className="font-bold text-purple-700">{syncPreview.a_agregar}</span></div>
              {syncPreview.muestra_agregar?.length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-xs text-gray-500">Ver primeros 20</summary>
                  <ul className="text-xs mt-1 text-gray-600">
                    {syncPreview.muestra_agregar.map((e) => <li key={e}>{e}</li>)}
                  </ul>
                </details>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sync Squarespace (Google Sheets) */}
      <Card className="border-blue-200 bg-blue-50/40">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Globe className="w-5 h-5 text-blue-600" /> Sincronizar suscriptores del sitio web
          </CardTitle>
          <CardDescription>
            Toma los emails que se cargaron en el formulario de suscripción de{" "}
            <b>colegiotslomas.org</b> (Squarespace → Google Sheets) y los agrega al grupo.
            Se ejecuta automáticamente cada 5 minutos. Este botón fuerza una corrida manual.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button
            onClick={runSheetSync}
            disabled={sheetSyncing}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="btn-sync-sheet"
          >
            {sheetSyncing ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Sincronizando...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-1" />
                Sincronizar ahora
              </>
            )}
          </Button>

          {sheetSyncResult && !sheetSyncResult.error && (
            <div className="text-sm bg-white border rounded-lg p-3 space-y-1">
              <div><b>Filas leídas:</b> {sheetSyncResult.total_filas ?? "-"}</div>
              <div><b>Agregadas al grupo:</b>{" "}
                <span className="text-green-700 font-bold">{sheetSyncResult.added ?? 0}</span>
              </div>
              <div><b>Ya estaban:</b> {sheetSyncResult.skipped ?? 0}</div>
              <div><b>Errores:</b> {sheetSyncResult.errors ?? 0}</div>
              {sheetSyncResult.mensaje && (
                <div className="text-xs text-gray-500 italic mt-1">{sheetSyncResult.mensaje}</div>
              )}
            </div>
          )}

          {sheetSyncResult?.error && (
            <div className="text-sm bg-red-50 border border-red-200 rounded-lg p-3 text-red-700">
              <AlertCircle className="w-4 h-4 inline mr-1" />
              <b>Error:</b> {sheetSyncResult.error}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Import CSV */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Upload className="w-5 h-5" /> Importar CSV masivo
          </CardTitle>
          <CardDescription>
            Sube un CSV con una columna de emails (detecta &apos;email&apos; o &apos;correo&apos; o toma la primera columna).
            Útil para migrar contactos externos (ej: CSV de un grupo viejo). Duplicados se ignoran.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2 items-center">
            <Input
              type="file"
              accept=".csv"
              onChange={(e) => setCsvFile(e.target.files?.[0])}
              className="max-w-md"
              data-testid="csv-input"
            />
            <Button
              onClick={uploadCsv}
              disabled={loading || !csvFile}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="btn-upload-csv"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
              Importar
            </Button>
          </div>

          {importResult && (
            <div className="text-sm bg-green-50 border border-green-200 rounded-lg p-3 space-y-1">
              <div className="flex items-center gap-2 font-medium text-green-800">
                <CheckCircle2 className="w-4 h-4" /> Import completado
              </div>
              <div>Procesados: <b>{importResult.total_procesados}</b></div>
              <div>Agregados: <b className="text-green-700">{importResult.added}</b></div>
              <div>Ya existían: {importResult.exists}</div>
              <div>Errores: {importResult.errors}</div>
              {importResult.detalle?.errors?.length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-xs text-red-600">
                    <AlertCircle className="w-3 h-3 inline mr-1" />
                    Ver errores
                  </summary>
                  <ul className="text-xs mt-1 text-red-700">
                    {importResult.detalle.errors.slice(0, 30).map((e, i) => (
                      <li key={i}>{e.email}: {e.error}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Agregar/quitar manual */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Agregar miembro manual</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 items-center">
            <Input
              type="email"
              placeholder="correo@ejemplo.com"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="max-w-md"
              onKeyDown={(e) => e.key === "Enter" && addOne()}
              data-testid="new-email-input"
            />
            <Button onClick={addOne} disabled={loading || !newEmail} data-testid="btn-add-one">
              Agregar al grupo
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Listado de miembros */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Miembros del grupo</CardTitle>
          <CardDescription>Lista completa de los {members.length} miembros actuales</CardDescription>
        </CardHeader>
        <CardContent>
          {members.length === 0 ? (
            <p className="text-gray-500 text-sm">Sin miembros o no cargados aún.</p>
          ) : (
            <div className="max-h-96 overflow-y-auto border rounded">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="text-left p-2">Email</th>
                    <th className="text-left p-2">Rol</th>
                    <th className="text-left p-2">Estado</th>
                    <th className="text-right p-2">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.id || m.email} className="border-b hover:bg-gray-50">
                      <td className="p-2 font-mono text-xs">{m.email}</td>
                      <td className="p-2">
                        <Badge variant={m.role === "OWNER" ? "default" : "secondary"} className="text-xs">
                          {m.role}
                        </Badge>
                      </td>
                      <td className="p-2 text-xs">{m.status}</td>
                      <td className="p-2 text-right">
                        {m.role !== "OWNER" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeMember(m.email)}
                            disabled={loading}
                            data-testid={`btn-remove-${m.email}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Audit log */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ClipboardList className="w-5 h-5" /> Historial de cambios
          </CardTitle>
          <CardDescription>Últimas 50 acciones sobre el grupo</CardDescription>
        </CardHeader>
        <CardContent>
          {audit.length === 0 ? (
            <p className="text-gray-500 text-sm">Sin registros aún.</p>
          ) : (
            <div className="max-h-72 overflow-y-auto border rounded text-xs">
              <table className="w-full">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="text-left p-2">Fecha</th>
                    <th className="text-left p-2">Acción</th>
                    <th className="text-left p-2">Email</th>
                    <th className="text-left p-2">Resultado</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.map((a, i) => (
                    <tr key={i} className="border-b">
                      <td className="p-2 text-gray-500">{new Date(a.fecha).toLocaleString("es-AR")}</td>
                      <td className="p-2">{a.accion}</td>
                      <td className="p-2 font-mono">{a.user}</td>
                      <td className="p-2">
                        <Badge
                          variant={a.resultado === "added" ? "default" : a.resultado === "error" ? "destructive" : "secondary"}
                          className="text-xs"
                        >
                          {a.resultado}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
